import { useEffect, useState } from 'react'
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from 'react-router-dom'

import { useUser } from 'app/User'
import { uploadImageToS3 } from 'lib/utils/uploadToS3'
import { useCaptureTrayContext } from '../CaptureTray.context'
import {
  useCreateTrayAnalysisMutation,
  useEditSubTrayItemMutation,
  useGeneratePresignedUrlMutation,
} from 'lib/apollo/hooks'

import { TrayScrew } from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayVisualization/TrayVisualization.types'
import { Surgery, TrayAnalysisInput } from 'common/types'
import { BetterIDTrayScrew } from 'views/SPDLayout/SPD/SPD.types'
import { useAssignedDigitalTrays } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.context'
import useTrayVisualizationLogic from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayVisualization/TrayVisualization.logic'

import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import { enqueueSnackbar } from 'notistack'
import { getProductMatchingFunction } from 'utils/getProductMatchingFunction'
import { getTrayTypeFromJSONFileName } from 'utils/getTrayTypeFromJSONFileName'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'
import { updateTrayImageHistory } from 'lib/utils/updateTrayImageHistory'

import { UsagePlate } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.types'

export interface AnalysisResult {
  section_label: string
  column_index: number
  column_label: number
  row_index: number
  row_label: number
  removed: boolean
  wasted: boolean
}

interface AbortControllerType {
  controller: AbortController
  signal: AbortSignal
}

interface TemplateDataType {
  template_data: {
    [key: string]: {
      dimensions: { width: number; height: number }
      screw: any[]
      plate: { label: string; device_id: string; device_description: string }[]
      drill: any[]
    }
  }
  folder_name: string
  tray_type: string
}

const useSubmitTrayCapture = (
  externalTemplateData: TemplateDataType | null = null
) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { surgeryId } = useParams()
  const [searchParams] = useSearchParams()

  const { user, isRep } = useUser()
  const { trayPlates, setTrayPlates } = useSPD()
  const { createTrayAnalysis } = useCreateTrayAnalysisMutation()
  const { setBaseImage, setIsSubmitting, setIsAlertOpen, setAlertData } =
    useCaptureTrayContext()
  const { generatePresignedUrl } = useGeneratePresignedUrlMutation()
  const {
    setUsageScrews,
    setUsagePlates,
    selectedSubTray,
    setSelectedSubTray,
  } = useAssignedDigitalTrays()
  const { findScrew } = useTrayVisualizationLogic()
  const { editSubTrayItem } = useEditSubTrayItemMutation()

  const [currentTemplateData, setCurrentTemplateData] =
    useState<TemplateDataType | null>(null)
  const [templateData, setTemplateData] = useState<TemplateDataType | null>(
    externalTemplateData
  )

  const [isAnalysisFromSPD, setIsAnalysisFromSPD] = useState<boolean>(false)
  const [isComplete, setIsComplete] = useState<boolean>(false)
  const [analysisScrews, setAnalysisScrews] = useState<AnalysisResult[]>([])
  const [abortController, setAbortController] = useState<AbortControllerType>(
    () => {
      const controller = new AbortController()
      return { controller, signal: controller.signal }
    }
  )
  const [aborted, setAborted] = useState<boolean>(false)
  const [analysisError, setAnalysisError] = useState<string>('')
  const [analysisFileName, setAnalysisFileName] = useState('Stryker_2.0MP')

  const matchScrewsToProductData = getProductMatchingFunction(
    getTrayTypeFromJSONFileName(analysisFileName)
  )
  const subTrayDetails = extractSubTrayJSONData(selectedSubTray)
  const isInTrayManagement =
    pathname.includes('tray-management') && !pathname.includes('add-tray')
  const isInOR = searchParams.get('mode') === 'analyze'

  useEffect(() => {
    if (externalTemplateData) {
      setTemplateData(externalTemplateData)
    }
  }, [externalTemplateData])

  useEffect(() => {
    if (templateData) {
      setCurrentTemplateData(templateData)
    }
  }, [templateData])

  useEffect(() => {
    if (aborted) {
      const controller = new AbortController()
      setAbortController({ controller, signal: controller.signal })
      setAborted(false)
    }
  }, [aborted])

  const saveAnalysis = async (inputData: TrayAnalysisInput) => {
    try {
      const result = await createTrayAnalysis(inputData)
      if (result.error) {
        if (process.env.REACT_APP_NODE_ENV !== 'production') {
          console.error('Error creating tray analysis:', result.error)
        }
      } else {
        localStorage.setItem('analysis-data', JSON.stringify({}))
        localStorage.setItem('analysis-data', JSON.stringify(result.data))
        setBaseImage(1890)
      }
    } catch (error) {
      console.error('Error creating tray analysis:', error)
    } finally {
      requestAnimationFrame(() => {
        setTimeout(() => {
          setIsSubmitting(false)
        }, 150)
      })
    }
  }

  const submitImage = async (image: string) => {
    try {
      setIsSubmitting(true)

      const imageUrl = (await handleBase64UploadToS3(image)) as string | null

      if (imageUrl) {
        handleSubmit(imageUrl)
      } else {
        handleSubmissionError('Failed to submit image. Please try again.')
      }
    } catch (error) {
      console.error('Error submitting image:', error)
      handleSubmissionError('An unexpected error occurred. Please try again.')
    }
  }

  const base64ToFile = (
    base64Data: string,
    filename: string,
    contentType: string
  ): File => {
    const byteCharacters = Buffer.from(
      base64Data.split(',')[1],
      'base64'
    ).toString('binary')
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512)

      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }

    const blob = new Blob(byteArrays, { type: contentType })
    return new File([blob], filename, { type: contentType })
  }

  const handleBase64UploadToS3 = async (base64Image: string) => {
    try {
      const file = base64ToFile(base64Image, 'image.jpg', 'image/jpeg')
      const key = `tray_images/${Date.now()}_${file.name}`

      const presignedUrl = (await generatePresignedUrl(key)) as string
      const imageUrl = await uploadImageToS3(file, presignedUrl, key)

      return imageUrl
    } catch (error) {
      console.error('Error uploading image to S3:', error)
      setIsSubmitting(false)
      return null
    }
  }

  const handleSaveNewTrayImage = async (image: string) => {
    if (!selectedSubTray || !subTrayDetails) return

    try {
      const updatedTrayDetails = updateTrayImageHistory(
        subTrayDetails,
        image,
        user?.email || '',
        isInTrayManagement ? (isRep ? 'rep' : 'spd') : 'nurse'
      )

      const newProductDetails = JSON.stringify(updatedTrayDetails)

      const editResult = await editSubTrayItem({
        id: Number(selectedSubTray.id),
        productDetails: newProductDetails,
      })

      if (editResult) {
        setSelectedSubTray((prev) =>
          prev
            ? {
                ...prev,
                productDetails: newProductDetails,
              }
            : null
        )
        enqueueSnackbar('Successfully saved image', {
          variant: 'success',
        })

        if (isInTrayManagement) {
          navigate(-1)
        } else {
          navigate('../tray-visualization')
        }
        return
      }
    } catch (error) {
      enqueueSnackbar('Error saving image', {
        variant: 'error',
      })
      console.error('Error saving new tray image:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (image: string) => {
    try {
      if (searchParams.get('mode') === 'image-only' || isInTrayManagement) {
        handleSaveNewTrayImage(image)
        return
      }

      const startTime = performance.now()

      const res = await fetch(
        `${process.env.REACT_APP_DTM_COMPUTER_VISION_API}/api/process_image`,
        {
          method: 'POST',
          signal: abortController.signal,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            after_image_url: image,
            template_filename: currentTemplateData?.folder_name || analysisFileName,
          }),
        }
      )

      const endTime = performance.now()

      const analysisDuration = Math.floor((endTime - startTime) / 1000)

      const data = await res.json()

      if (data.result?.regions) {
        const trayType = currentTemplateData?.tray_type
        const templateData = currentTemplateData?.template_data

        const plateRegions = data.result.regions.filter(
          (region: any) => region.type === 'plate'
        )

        plateRegions.forEach((region: any) => {
          const formattedTrayPlates = region.areas
            .map((area: any) => {
              if (!area.label) return null

              if (!templateData) return null

              const firstKey = Object.keys(templateData)[0]
              const templatePlate = templateData[firstKey].plate?.find(
                (plate: any) => plate.label === area.label
              )

              return {
                deviceId: templatePlate?.device_id,
                deviceDescription: templatePlate?.device_description,
                plateName: area.label,
                plateCount: area.count || 0,
              }
            })
            .filter(Boolean)

          if (isInOR) {
            // Handle setting usage plates when in OR
            const usedPlates: UsagePlate[] = []

            // Compare each formatted plate with existing tray plates
            formattedTrayPlates.forEach((formattedPlate: any) => {
              const existingPlate = trayPlates.find(
                (plate) => plate.plateName === formattedPlate.plateName
              )

              if (existingPlate) {
                // Calculate the count difference
                const countDifference =
                  existingPlate.plateCount - formattedPlate.plateCount

                // If count is lower in formatted plates, add to usage plates
                if (countDifference > 0) {
                  // Add plate to usagePlates array countDifference times
                  for (let i = 0; i < countDifference; i++) {
                    usedPlates.push({
                      deviceId: existingPlate.deviceId || '',
                      plateName: existingPlate.plateName,
                      wasted: false,
                    })
                  }
                }
              }
            })

            // Set the usage plates
            setUsagePlates(usedPlates)
          }

          setTrayPlates(formattedTrayPlates)
        })
      }

      if (data.error?.length > 0) {
        console.error('Error: ', data.error)
        setAnalysisError('Tray analysis failed. Please try again.')
        enqueueSnackbar('Tray analysis failed. Please try again.', {
          variant: 'error',
        })
        handleSubmissionError('Tray analysis failed. Please try again.')
        await saveAndNavigate(image, [], analysisDuration, data.error)
        return
      }

      if (!res.ok) {
        throw new Error('Submission failed')
      }

      if (data.tray_analysis?.tray_info?.tray_mfr === 'Zimmer') {
        setIsComplete(true)

        const newTrayAnalysis: TrayAnalysisInput = {
          tray_img: image,
          analysis_results: [],
          user_corrections: [],
          surgery_id: surgeryId as Surgery['_id'],
          user_id: user?.id as string,
          error: 'no error found',
          processing_time: analysisDuration,
          accuracy_rate: 100,
        }
        await saveAnalysis(newTrayAnalysis)
        return
      }

      const screwsFromAnalysis = removeDuplicateScrews(data.tray_analysis)

      const analysisResultScrews = extractTrayScrews(
        screwsFromAnalysis
          .filter((screw: AnalysisResult) => screw.removed === false)
          .map((screw: AnalysisResult) => ({
            label: screw.section_label,
            x: screw.column_index,
            column: screw.column_label,
            row: screw.row_index,
            wasted: screw.wasted,
          }))
      )
      setAnalysisScrews(analysisResultScrews)

      const trayScrews = extractTrayScrews(
        screwsFromAnalysis
          .filter((screw: AnalysisResult) => screw.removed === true)
          .map((screw: AnalysisResult) => ({
            label: screw.section_label,
            x: screw.column_index,
            column: screw.column_label,
            row: screw.row_index,
            wasted: screw.wasted,
          }))
      )

      await saveAndNavigate(image, trayScrews, analysisDuration)
    } catch (error) {
      console.error(error)

      if (error instanceof Error && error.name === 'AbortError') {
        return
      }

      setAnalysisError(
        'Analysis failed. Please try again with a different image'
      )
      enqueueSnackbar(
        'Analysis failed. Please try again with a different image',
        {
          variant: 'error',
        }
      )

      handleSubmissionError(
        'Analysis failed. Please try again with a different image'
      )
    }
  }

  const removeDuplicateScrews = (screws: AnalysisResult[]) => {
    const uniqueObjectsMap = new Map<string, AnalysisResult>()

    for (const obj of screws) {
      const key = `${obj.column_index}_${obj.row_index}_${obj.section_label}`
      uniqueObjectsMap.set(key, obj)
    }

    return Array.from(uniqueObjectsMap.values())
  }

  const handleSubmissionError = (errorMessage?: string) => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        setIsSubmitting(false)
      }, 150)
    })
    setIsAlertOpen(true)
    setAlertData({
      description: errorMessage || 'Please try again with a different image.',
      mode: 'error',
    })
  }

  const handleCancellation = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        setIsSubmitting(false)
      }, 150)
    })
  }

  const extractTrayScrews = (data: any) => {
    localStorage.setItem('tray-products', JSON.stringify(data))
    const trayData = localStorage.getItem('tray-products') as string
    return JSON.parse(trayData)
  }

  const saveAndNavigate = async (
    image: string,
    trayScrews: TrayScrew[],
    processingTime: number,
    error: string = 'no error found'
  ) => {
    try {
      const analysisResults = trayScrews.map((screw: any) => ({
        column: screw.x,
        size: screw.column,
        label: screw.label,
        row: screw.row,
        wasted: screw.wasted,
      }))

      const newUsageScrews = trayScrews
        .map((trayScrew) => {
          const screwFromSPD = findScrew(
            trayScrew.label,
            trayScrew.row,
            trayScrew.x
          )

          if (screwFromSPD) {
            const productData = matchScrewsToProductData(
              trayScrew.label,
              trayScrew.column
            )

            return {
              ...screwFromSPD,
              deviceId: productData.deviceID,
              deviceDescription: productData.title,
              company: {
                name: 'Stryker',
                id: 0,
                regEmail: '',
              },
              expirationDate: null,
            }
          }

          return screwFromSPD || null
        })
        .filter((screw) => screw !== null) as BetterIDTrayScrew[]

      setUsageScrews(newUsageScrews)

      const newTrayAnalysis: TrayAnalysisInput = {
        tray_img: image,
        analysis_results: analysisResults,
        user_corrections: [],
        surgery_id: surgeryId as Surgery['_id'],
        user_id: user?.id as string,
        error,
        processing_time: processingTime,
        accuracy_rate: 100,
      }

      await saveAnalysis(newTrayAnalysis)

      if (error === 'no error found') {
        setIsComplete(true)
        if (selectedSubTray) {
          if (subTrayDetails) {
            const updatedTrayDetails = updateTrayImageHistory(
              subTrayDetails,
              image,
              user?.email || '',
              'nurse'
            )

            const newProductDetails = JSON.stringify(updatedTrayDetails)

            editSubTrayItem({
              id: Number(selectedSubTray.id),
              productDetails: newProductDetails,
            })

            setSelectedSubTray((prev) =>
              prev ? { ...prev, productDetails: newProductDetails } : null
            )
          }
        }
        if (!isAnalysisFromSPD) {
          navigate('../tray-visualization')
        }
        return
      }
    } catch (error) {
      console.error('Failed to save analysis and navigate')
    }
  }

  const cancelSubmit = () => {
    abortController.controller.abort()
    setAborted(true)
    handleCancellation()
  }

  const resetSubmitTrayCapture = () => {
    setIsAnalysisFromSPD(false)
    setIsComplete(false)
    setAnalysisScrews([])
    setAnalysisError('')
    setAnalysisFileName('Stryker_2.0MP')
  }

  const updateTemplateData = (data: TemplateDataType | null) => {
    setTemplateData(data)
  }

  return {
    handleSubmit,
    submitImage,
    cancelSubmit,
    isAnalysisFromSPD,
    setIsAnalysisFromSPD,
    isComplete,
    analysisScrews,
    handleBase64UploadToS3,
    setIsComplete,
    analysisError,
    setAnalysisError,
    base64ToFile,
    analysisFileName,
    setAnalysisFileName,
    resetSubmitTrayCapture,
    currentTemplateData,
    updateTemplateData,
  }
}

export default useSubmitTrayCapture
